import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AccountActivity from "../components/AccountActivity";
import LogoUpload from "../components/LogoUpload";
import { updateUser } from "../store/slices/authSlice";
import { useSnackbar } from "notistack";
import ProfileManagement from "./ProfileManagement";
import axios from "axios";

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    // Check if user data needs to be reloaded
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser && (!user || user.logoUrl !== storedUser.logoUrl)) {
      dispatch(updateUser(storedUser));
    }
  }, [dispatch, user]);

  // Add this validation function at the top of your component
  const validatePassword = (password) => {
    // Match backend validation exactly - only accepting @$!%*?& special characters
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  // Then update your handlePasswordChange function
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Check if passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t("auth.profile.passwordMismatch"));
      setLoading(false);
      return;
    }

    // Validate password strength
    if (!validatePassword(formData.newPassword)) {
      setError(
        "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character (@, $, !, %, *, ?, or &)."
      );
      setLoading(false);
      return;
    }

    try {
      // Make the actual API call
      const response = await axios.put(
        "/api/user/change-password",
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data.success) {
        setSuccess(true);
        enqueueSnackbar("Password changed successfully", {
          variant: "success",
        });
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (responseData) => {
    try {
      if (responseData.user) {
        // Update local user data
        dispatch(updateUser(responseData.user));
        // Ensure local storage is updated
        localStorage.setItem("user", JSON.stringify(responseData.user));
        setSuccess(true);
      }
    } catch (error) {
      console.error("Error updating user data:", error);
      enqueueSnackbar(t("auth.profile.logoUploadError"), { variant: "error" });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t("auth.profile.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("auth.profile.logoUploadSuccess")}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Logo Upload Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("auth.profile.companyLogo")}
            </Typography>
            <LogoUpload
              currentLogo={user?.logoUrl}
              onLogoUpload={handleLogoUpload}
              loading={loading}
            />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("auth.profile.businessProfiles")}
            </Typography>
            <ProfileManagement />
          </Paper>
        </Grid>
        {/* User Information Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("auth.profile.userInformation")}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography color="textSecondary">
                {t("auth.profile.username")}
              </Typography>
              <Typography variant="h6">{user?.username}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography color="textSecondary">
                {t("auth.profile.email")}
              </Typography>
              <Typography variant="h6">{user?.email}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography color="textSecondary">
                {t("auth.profile.role")}
              </Typography>
              <Typography variant="h6">{user?.role}</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Password Change Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("auth.profile.changePassword")}
            </Typography>

            <form onSubmit={handlePasswordChange}>
              <TextField
                fullWidth
                type="password"
                label={t("auth.profile.currentPassword")}
                value={formData.currentPassword}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    currentPassword: e.target.value,
                  })
                }
                margin="normal"
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                type="password"
                label={t("auth.profile.newPassword")}
                value={formData.newPassword}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    newPassword: e.target.value,
                  })
                }
                margin="normal"
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                type="password"
                label={t("auth.profile.confirmPassword")}
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    confirmPassword: e.target.value,
                  })
                }
                margin="normal"
                required
                disabled={loading}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t("auth.profile.changePassword")
                )}
              </Button>
            </form>
          </Paper>
        </Grid>

        {/* Account Activity Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("auth.profile.accountActivity")}
            </Typography>
            <AccountActivity />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
